// Workflow Builder v2 — pure reducer for WorkflowSession state.
//
// The reducer is side-effect-free and synchronous. Persistence is the hook's
// responsibility (see useWorkflowSession). Keeping the reducer pure makes it
// trivially testable and lets the hook decide which mutations to write
// immediately vs debounce.

import {
  type AnalysisPayload,
  type DraftErrorState,
  type PastedOutputPayload,
  type PlanDiffOp,
  type PromptDraftPayload,
  type ProposalPayload,
  type StepDraft,
  type StepPhase,
  type StepState,
  type StreamMessage,
  type WorkflowCadence,
  type WorkflowPhase,
  type WorkflowSession,
  generateId,
  nowIso,
  sortStepsByPosition,
} from './workflowStreamProtocol';

// ── Actions ──────────────────────────────────────────────────────────

export type WorkflowAction =
  | { type: 'hydrate'; session: WorkflowSession }
  | { type: 'set_workflow_phase'; phase: WorkflowPhase }
  | { type: 'set_active_step'; stepId: string | null }
  | { type: 'set_name'; name: string }
  | { type: 'set_meta'; objective?: string; cadence?: WorkflowCadence }
  | { type: 'seed_from_proposal'; proposal: ProposalPayload }
  | { type: 'apply_plan_diff'; ops: PlanDiffOp[] }
  | { type: 'set_step_phase'; stepId: string; phase: StepPhase }
  | { type: 'set_prompt_draft'; stepId: string; draft: PromptDraftPayload }
  | { type: 'set_pasted_output'; stepId: string; output: PastedOutputPayload }
  | { type: 'set_analysis'; stepId: string; analysis: AnalysisPayload }
  | { type: 'set_draft_error'; stepId: string; error: DraftErrorState | null }
  | { type: 'record_decision'; stepId: string; decision: { question: string; chosen: string } }
  | { type: 'set_selected_tool'; tool: string }
  | { type: 'set_step_score'; stepId: string; scoreId: string; submissionId: string }
  | { type: 'append_message'; message: StreamMessage }
  | { type: 'remove_messages'; ids: string[] }
  | { type: 'mark_finished' };

// ── Helpers ──────────────────────────────────────────────────────────

function stepFromDraft(draft: StepDraft, position: number): StepState {
  return {
    id: draft.id ?? generateId(),
    position,
    stepName: draft.stepName,
    description: draft.description,
    type: draft.type,
    recommendedTool: draft.recommendedTool,
    estimatedDuration: draft.estimatedDuration,
    inputs: draft.inputs ?? [],
    outputs: draft.outputs ?? [],
    handoffInstruction: draft.handoffInstruction,
    phase: 'proposed',
    promptHistory: [],
    decisions: [],
    createdAt: nowIso(),
  };
}

function applyOp(steps: StepState[], op: PlanDiffOp): StepState[] {
  switch (op.op) {
    case 'add_step': {
      const newStep = stepFromDraft(op.step, op.position);
      const out = [...steps];
      out.splice(op.position, 0, newStep);
      // Reindex positions after insert
      return out.map((s, i) => (s.position === i ? s : { ...s, position: i }));
    }
    case 'remove_step': {
      const target = steps.find(s => s.id === op.stepId);
      // Guardrail: never remove a completed step. Edge function also guards;
      // this is defense-in-depth.
      if (!target || target.phase === 'done') return steps;
      const out = steps.filter(s => s.id !== op.stepId);
      return out.map((s, i) => (s.position === i ? s : { ...s, position: i }));
    }
    case 'replace_step': {
      const target = steps.find(s => s.id === op.stepId);
      if (!target || target.phase === 'done') return steps;
      const replacement: StepState = {
        ...stepFromDraft(op.step, target.position),
        id: target.id, // preserve id so refs survive
      };
      return steps.map(s => (s.id === op.stepId ? replacement : s));
    }
    case 'reorder': {
      const byId = new Map(steps.map(s => [s.id, s]));
      const next: StepState[] = [];
      op.stepIds.forEach((id, i) => {
        const s = byId.get(id);
        if (s) next.push({ ...s, position: i });
      });
      // Append any steps not mentioned (defensive)
      steps.forEach(s => {
        if (!op.stepIds.includes(s.id)) next.push({ ...s, position: next.length });
      });
      return next;
    }
    case 'update_step': {
      return steps.map(s => {
        if (s.id !== op.stepId) return s;
        const { id: _ignored, ...patch } = op.patch;
        void _ignored;
        return { ...s, ...patch };
      });
    }
  }
}

// ── Reducer ──────────────────────────────────────────────────────────

export function workflowSessionReducer(
  state: WorkflowSession,
  action: WorkflowAction,
): WorkflowSession {
  switch (action.type) {
    case 'hydrate':
      return { ...action.session, steps: sortStepsByPosition(action.session.steps) };

    case 'set_workflow_phase':
      return { ...state, phase: action.phase };

    case 'set_active_step':
      return { ...state, activeStepId: action.stepId };

    case 'set_name':
      return { ...state, name: action.name };

    case 'set_meta':
      return {
        ...state,
        objective: action.objective ?? state.objective,
        cadence: action.cadence ?? state.cadence,
      };

    case 'seed_from_proposal': {
      const steps = action.proposal.steps.map((d, i) => stepFromDraft(d, i));
      return {
        ...state,
        name: action.proposal.name,
        objective: action.proposal.objective,
        cadence: action.proposal.cadence,
        steps,
        activeStepId: steps[0]?.id ?? null,
      };
    }

    case 'apply_plan_diff': {
      const nextSteps = action.ops.reduce(applyOp, state.steps);
      // If active step was removed, clear pointer
      const activeStillExists = state.activeStepId
        ? nextSteps.some(s => s.id === state.activeStepId)
        : true;
      return {
        ...state,
        steps: nextSteps,
        activeStepId: activeStillExists ? state.activeStepId : (nextSteps[0]?.id ?? null),
      };
    }

    case 'set_step_phase':
      return {
        ...state,
        steps: state.steps.map(s => {
          if (s.id !== action.stepId) return s;
          const next: StepState = { ...s, phase: action.phase };
          if (action.phase === 'prompt_drafting' && !s.startedAt) next.startedAt = nowIso();
          if (action.phase === 'done' && !s.completedAt) next.completedAt = nowIso();
          // Iterating: going back to prompt_ready clears stale output/analysis
          // so the canvas doesn't show data from the previous attempt.
          if (action.phase === 'prompt_ready') {
            next.pastedOutput = undefined;
            next.analysis = undefined;
            next.completedAt = undefined;
          }
          return next;
        }),
      };

    case 'set_prompt_draft':
      return {
        ...state,
        steps: state.steps.map(s => {
          if (s.id !== action.stepId) return s;
          // Push previous draft onto history before replacing
          const promptHistory = s.promptDraft
            ? [...s.promptHistory, s.promptDraft]
            : s.promptHistory;
          return {
            ...s,
            promptDraft: action.draft,
            promptHistory,
            phase: 'prompt_ready',
          };
        }),
      };

    case 'set_pasted_output':
      return {
        ...state,
        steps: state.steps.map(s =>
          s.id === action.stepId
            ? { ...s, pastedOutput: action.output, phase: 'analyzing' }
            : s,
        ),
      };

    case 'set_analysis':
      return {
        ...state,
        steps: state.steps.map(s =>
          s.id === action.stepId
            ? { ...s, analysis: action.analysis, phase: 'done', completedAt: s.completedAt ?? nowIso() }
            : s,
        ),
      };

    case 'set_draft_error':
      return {
        ...state,
        steps: state.steps.map(s =>
          s.id === action.stepId
            ? { ...s, draftError: action.error ?? undefined }
            : s,
        ),
      };

    case 'set_step_score':
      return {
        ...state,
        steps: state.steps.map(s =>
          s.id === action.stepId
            ? { ...s, scoreId: action.scoreId, submissionId: action.submissionId }
            : s,
        ),
      };

    case 'record_decision':
      return {
        ...state,
        steps: state.steps.map(s =>
          s.id === action.stepId
            ? { ...s, decisions: [...s.decisions, action.decision] }
            : s,
        ),
      };

    case 'set_selected_tool':
      return {
        ...state,
        selectedTool: action.tool,
        steps: state.steps.map(s => ({ ...s, recommendedTool: action.tool })),
      };

    case 'append_message':
      return { ...state, messages: [...state.messages, action.message] };

    case 'remove_messages': {
      if (action.ids.length === 0) return state;
      const drop = new Set(action.ids);
      return { ...state, messages: state.messages.filter(m => !drop.has(m.id)) };
    }

    case 'mark_finished':
      return { ...state, phase: 'finished', completedAt: nowIso() };
  }
}

// ── Action ⇒ persistence hint ─────────────────────────────────────────
// Used by useWorkflowSession to decide what to write to Postgres and at
// what cadence. Returned shape names the tables/columns affected.

export type PersistTarget =
  | { table: 'workflow_conversations'; columns: readonly (keyof WorkflowSession)[]; debounceMs: number }
  | { table: 'workflow_steps'; stepId: string; columns: readonly string[]; debounceMs: number };

export function persistTargetsForAction(action: WorkflowAction): PersistTarget[] {
  switch (action.type) {
    case 'hydrate':
      return [];
    case 'set_workflow_phase':
      return [{ table: 'workflow_conversations', columns: ['phase'], debounceMs: 0 }];
    case 'set_active_step':
      return [{ table: 'workflow_conversations', columns: ['activeStepId'], debounceMs: 0 }];
    case 'set_name':
      return [{ table: 'workflow_conversations', columns: ['name'], debounceMs: 400 }];
    case 'set_meta':
      return [{ table: 'workflow_conversations', columns: ['objective', 'cadence'], debounceMs: 400 }];
    case 'seed_from_proposal':
      // Caller is expected to also write all the workflow_steps rows.
      return [{ table: 'workflow_conversations', columns: ['name', 'objective', 'cadence', 'activeStepId'], debounceMs: 0 }];
    case 'apply_plan_diff':
      // Plan diffs may touch many rows. The hook will batch.
      return [];
    case 'set_step_phase':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['phase', 'started_at', 'completed_at', 'pasted_output_text', 'output_meta_json', 'analysis_json'], debounceMs: 0 }];
    case 'set_prompt_draft':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['prompt_draft_json', 'prompt_history_json', 'phase'], debounceMs: 400 }];
    case 'set_pasted_output':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['pasted_output_text', 'output_meta_json', 'phase'], debounceMs: 400 }];
    case 'set_analysis':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['analysis_json', 'phase', 'completed_at'], debounceMs: 0 }];
    case 'set_draft_error':
      // draftError is transient (in-memory only) — no persistence.
      return [];
    case 'set_step_score':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['submission_id', 'score_id'], debounceMs: 0 }];
    case 'record_decision':
      return [{ table: 'workflow_steps', stepId: action.stepId, columns: ['decisions_json'], debounceMs: 400 }];
    case 'set_selected_tool':
      // Writes workflow-level selected_tool AND propagates to all step rows.
      // Handled like seed_from_proposal in the hook (batch upsert).
      return [{ table: 'workflow_conversations', columns: ['selectedTool'], debounceMs: 0 }];
    case 'append_message':
      return [{ table: 'workflow_conversations', columns: ['messages'], debounceMs: 400 }];
    case 'remove_messages':
      return [{ table: 'workflow_conversations', columns: ['messages'], debounceMs: 400 }];
    case 'mark_finished':
      return [{ table: 'workflow_conversations', columns: ['phase', 'completedAt'], debounceMs: 0 }];
  }
}
